// Hashtag pill chip overlay — measurement + Canvas render.
// Ports core/renderer/doit_overlay/chip.py.

import {
  CHIP_STYLE_PRESETS,
  HASHTAG_HEIGHT_FRAC,
  HASHTAG_PAD_X_FRAC,
  HASHTAG_PAD_Y_FRAC,
  THEME_COLORS,
  type ChipStyle,
} from "./constants.generated.js";
import { anchorXY, type Position } from "./positions.js";

/** Font choices exposed in the UI dropdown. */
export type ChipFontKey = "microsoft-jhenghei" | "gensen-rounded";

/**
 * Map ChipFontKey → CSS font-family stack passed to Canvas `ctx.font`.
 *
 * - microsoft-jhenghei: system font, available on Windows by default;
 *   on Mac/Linux falls through to PingFang TC / Noto Sans TC / sans-serif.
 * - gensen-rounded: loaded via @font-face in style.css (woff2 from
 *   ButTaiwan/gensen-font, fetched by scripts/fetch_gensen_font.py).
 */
export const CHIP_FONT_FAMILY: Record<ChipFontKey, string> = {
  "microsoft-jhenghei":
    `"Microsoft JhengHei", "PingFang TC", "Noto Sans TC", sans-serif`,
  "gensen-rounded":
    `"GenSenRounded W7", "Microsoft JhengHei", "Noto Sans TC", sans-serif`,
};

const HEX_RE = /^#[0-9A-Fa-f]{6}$/;

function validatedHex(hex: string): string {
  if (!HEX_RE.test(hex)) {
    throw new Error(`invalid hex color: ${JSON.stringify(hex)} (want '#RRGGBB')`);
  }
  return hex; // Canvas accepts #RRGGBB directly.
}

export function resolveChipColor(
  theme: string | null,
  custom: string | null,
): string {
  if (custom) {
    if (!HEX_RE.test(custom)) {
      throw new Error(`custom colour must be #RRGGBB, got ${JSON.stringify(custom)}`);
    }
    return custom;
  }
  const key = theme ?? "future_life";
  const hex = THEME_COLORS[key];
  if (!hex) {
    throw new Error(`unknown theme ${JSON.stringify(key)}`);
  }
  return hex;
}

interface ChipMetrics {
  chipW: number;
  chipH: number;
  fontSize: number;
  fontSpec: string; // full ctx.font string
  textWidth: number;
  textLeftBearing: number;
  textRightBearing: number;
  textAscent: number;
  textDescent: number;
}

function measureChip(
  canvasW: number,
  canvasH: number,
  text: string,
  fontKey: ChipFontKey,
  style: ChipStyle,
): ChipMetrics {
  const preset = CHIP_STYLE_PRESETS[style];
  if (!preset) {
    throw new Error(`unknown chip style ${JSON.stringify(style)}`);
  }
  const shortEdge = Math.min(canvasW, canvasH);
  const chipH = Math.max(20, Math.round(shortEdge * HASHTAG_HEIGHT_FRAC));
  const fontSize = Math.max(10, Math.round(chipH * preset.font_frac));
  const fontSpec = `700 ${fontSize}px ${CHIP_FONT_FAMILY[fontKey]}`;

  // Need a 2D context to measure. We use an OffscreenCanvas temporarily.
  const probe = new OffscreenCanvas(8, 8).getContext("2d");
  if (!probe) {
    throw new Error("could not acquire 2d context for text measurement");
  }
  probe.font = fontSpec;
  const m = probe.measureText(text);
  // Mirror Python PIL.getbbox(): bbox[2] - bbox[0] for text width incl. bearings.
  const textLeftBearing = m.actualBoundingBoxLeft ?? 0;
  const textRightBearing = m.actualBoundingBoxRight ?? m.width;
  const textWidth = textLeftBearing + textRightBearing;
  const textAscent = m.actualBoundingBoxAscent ?? fontSize * 0.8;
  const textDescent = m.actualBoundingBoxDescent ?? fontSize * 0.2;

  const padX = Math.round(chipH * preset.padding_x_frac);
  const chipW = textWidth + padX * 2;

  return { chipW, chipH, fontSize, fontSpec, textWidth, textLeftBearing, textRightBearing, textAscent, textDescent };
}

export interface OverlayHashtagOptions {
  text: string;
  position: Position;
  fillHex: string;
  textHex: string;
  style: ChipStyle;
  fontKey: ChipFontKey;
}

/**
 * Composite a rounded-pill hashtag chip onto `canvas`.
 *
 * The pill auto-sizes to text. Geometry is driven by canvas short edge
 * so the result scales identically across input resolutions and aspects.
 */
export function overlayHashtag(
  canvas: OffscreenCanvas,
  ctx: OffscreenCanvasRenderingContext2D,
  opts: OverlayHashtagOptions,
): OffscreenCanvas {
  const m = measureChip(canvas.width, canvas.height, opts.text, opts.fontKey, opts.style);
  const { x, y } = anchorXY(
    canvas.width,
    canvas.height,
    m.chipW,
    m.chipH,
    opts.position,
    HASHTAG_PAD_X_FRAC,
    HASHTAG_PAD_Y_FRAC,
  );

  const radius = m.chipH / 2; // pill: r = h/2

  ctx.save();
  ctx.beginPath();
  // Use roundRect (modern browsers — Chrome 99+, Firefox 113+, Safari 16+).
  ctx.fillStyle = validatedHex(opts.fillHex);
  if (typeof ctx.roundRect === "function") {
    ctx.roundRect(x, y, m.chipW, m.chipH, radius);
  } else {
    // Fallback: manual pill path
    drawRoundRect(ctx, x, y, m.chipW, m.chipH, radius);
  }
  ctx.fill();

  // Text — centred via bbox math (matches Python which avoids PIL anchors
  // because Chinese fonts often drift).
  ctx.font = m.fontSpec;
  ctx.fillStyle = validatedHex(opts.textHex);
  ctx.textBaseline = "alphabetic";
  const textH = m.textAscent + m.textDescent;
  const tx = x + (m.chipW - m.textWidth) / 2 + m.textLeftBearing;
  const ty = y + (m.chipH - textH) / 2 + m.textAscent;
  ctx.fillText(opts.text, tx, ty);
  ctx.restore();

  return canvas;
}

function drawRoundRect(
  ctx: OffscreenCanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
}
